import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Power, PowerOff, Trophy } from 'lucide-react';
import { checkInService } from '@/services/checkin.service';
import { MilestoneFormModal } from './components/milestone-form-modal';
import { toast } from 'sonner';

export function CheckInConfigPage() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

  // Fetch milestones
  const { data, isLoading } = useQuery({
    queryKey: ['checkin-milestones'],
    queryFn: () =>
      checkInService.getMilestones({
        maxResultCount: 100,
        skipCount: 0,
      }),
  });

  // Delete milestone
  const deleteMutation = useMutation({
    mutationFn: (id: string) => checkInService.deleteMilestone(id),
    onSuccess: () => {
      toast.success('Milestone deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['checkin-milestones'] });
    },
    onError: () => {
      toast.error('Failed to delete milestone');
    },
  });

  // Toggle milestone active status
  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      checkInService.updateMilestone(id, { isActive } as any), // eslint-disable-line @typescript-eslint/no-explicit-any
    onSuccess: () => {
      toast.success('Milestone status updated');
      queryClient.invalidateQueries({ queryKey: ['checkin-milestones'] });
    },
    onError: () => {
      toast.error('Failed to update milestone status');
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete milestone "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const milestones = data?.items || [];
  const activeMilestones = milestones.filter((m) => m.isActive).length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Check-In Configuration</h1>
          <p className="text-muted-foreground mt-1">Manage check-in milestones and rewards</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Milestone
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Milestones</p>
            <p className="text-3xl font-bold">{milestones.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Active Milestones</p>
            <p className="text-3xl font-bold text-success">{activeMilestones}</p>
          </CardContent>
        </Card>
      </div>

      {/* Milestones Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Check-In Milestones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse h-16 bg-muted rounded" />
              ))}
            </div>
          ) : milestones.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Milestone</TableHead>
                  <TableHead className="text-right">Day Count</TableHead>
                  <TableHead className="text-right">Token Amount</TableHead>
                  <TableHead className="text-right">Bonus Multiplier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {milestones.map((milestone) => (
                  <TableRow key={milestone.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{milestone.milestoneName}</p>
                        {milestone.description && (
                          <p className="text-sm text-muted-foreground">{milestone.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{milestone.dayCount} days</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      {milestone.tokenAmount}
                    </TableCell>
                    <TableCell className="text-right">
                      {milestone.bonusMultiplier ? (
                        <Badge variant="secondary">{milestone.bonusMultiplier}x</Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          toggleMutation.mutate({
                            id: milestone.id,
                            isActive: !milestone.isActive,
                          })
                        }
                        disabled={toggleMutation.isPending}
                      >
                        {milestone.isActive ? (
                          <>
                            <Power className="w-4 h-4 text-success mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <PowerOff className="w-4 h-4 text-muted-foreground mr-1" />
                            Inactive
                          </>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingMilestone(milestone)}
                        title="Edit Milestone"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(milestone.id, milestone.milestoneName)}
                        title="Delete Milestone"
                      >
                        <Trash2 className="w-4 h-4 text-danger" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No milestones configured</p>
              <p className="text-sm mt-2">Create your first check-in milestone to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingMilestone) && (
        <MilestoneFormModal
          open={showCreateModal || !!editingMilestone}
          onClose={() => {
            setShowCreateModal(false);
            setEditingMilestone(null);
          }}
          milestone={editingMilestone}
        />
      )}
    </div>
  );
}

export default CheckInConfigPage;
